import { arrayMove } from 'react-sortable-hoc';

import update from 'immutability-helper';

/**
 * By default, each Attribute columns will get 5 new values
 * @type {Number}
 */
export const DefaultNewAttributesCount = 5;

/**
 * Add an empty input for an attribute
 * @param {Object} attributeObj
 * @param {String} attribute - name of the attribute
 * @returns {Object}
 */
export const addNewValue = (attributeObj, attribute) => {
  let key = 0;

  let newKey = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0, ii = attributeObj.data.length; i < ii; ++i) {
    if (attributeObj.data[i].key >= key) {
      key = attributeObj.data[i].key + 1;
    }
    if (attributeObj.data[i].type === 'new') {
      // eslint-disable-next-line no-plusplus
      newKey++;
    }
  }

  return update(attributeObj, {
    data: {
      $push: [
        {
          number: attributeObj.number,
          name: attribute,
          key,
          newKey,
          value: '',
          type: 'new',
        },
      ],
    },
  });
};

/**
 * Add a divider between the saved and new attribute inputs
 * @param {Object} attributeObj
 * @param {String} attribute - name of the attribute
 * @returns {Object}
 */
export const addDivider = (attributeObj, attribute) =>
  update(attributeObj, {
    data: {
      $push: [
        {
          name: attribute,
          type: 'divider',
        },
      ],
    },
  });

/**
 * For every Attributes, add empty value(s) at the end of the data Array
 * @param {Object} attributes
 * @param {Boolean} isEditable - customer has "product_edit" right
 * @returns {Object}
 */
export const addNewValues = (attributes, isEditable) => {
  let newAttributesCount;

  // eslint-disable-next-line
  for (const attribute in attributes) {
    newAttributesCount = DefaultNewAttributesCount;
    if (attributes[attribute].data.length) {
      newAttributesCount = isEditable ? 1 : 0;
      if (isEditable) {
        // eslint-disable-next-line no-param-reassign
        attributes[attribute] = addDivider(attributes[attribute], attribute);
      }
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0, ii = newAttributesCount; i < ii; ++i) {
      // eslint-disable-next-line no-param-reassign
      attributes[attribute] = addNewValue(attributes[attribute], attribute);
    }
  }
  return attributes;
};

/**
 * Set the attribute's order hidden field
 * @param {Object} state
 * @param {String} attribute - name of the attribute
 * @returns {Object}
 */
export const updateAttributeOrder = (attribute) => (state) => {
  const attributeObj = state.attributes[attribute];

  attributeObj.order = [];
  // eslint-disable-next-line array-callback-return
  attributeObj.data.forEach((datum) => {
    const attributeValue = datum.value?.trim();
    if (attributeValue?.length) {
      attributeObj.order.push(datum.key);
    }
  });

  return update(state, {
    attributes: {
      $merge: {
        [attribute]: attributeObj,
      },
    },
  });
};

/**
 * Validate if there is an empty input in the attribute list.
 * If there is none, add a new one.
 * @param {String} attribute - name of the attribute
 * @param {Number} [index] - index of the changed value
 * @param {String} [text] - new input value
 * @returns {Object}
 */
export const checkAddNewValue = (attribute, index, text) => (state) => {
  const attributeObj = state.attributes[attribute];

  let dividerIndex = 0;

  let hasEmptyValue = false;

  // eslint-disable-next-line no-plusplus
  for (let i = 0, ii = attributeObj.data.length; i < ii; ++i) {
    if (attributeObj.data[i].type === 'divider') {
      dividerIndex = i;
      break;
    }
  }

  if (typeof index !== 'undefined') {
    attributeObj.data[index].value = text;
  }

  // eslint-disable-next-line no-plusplus
  for (let i = dividerIndex, ii = attributeObj.data.length; i < ii; ++i) {
    if (attributeObj.data[i].value === '') {
      hasEmptyValue = true;
      break;
    }
  }

  // eslint-disable-next-line no-param-reassign
  state = updateAttributeOrder(attribute)(state);

  if (!hasEmptyValue) {
    return update(state, {
      attributes: {
        $merge: {
          [attribute]: addNewValue(state.attributes[attribute], attribute),
        },
      },
    });
  }

  return state;
};

/**
 * When the attribute is dropped, handle the order change
 * @param {Object} state
 * @param {String} attribute - name of the attribute
 * @param {Number} oldIndex - before drop index
 * @param {Number} newIndex - after drop index
 * @returns {Object}
 */
export const reorderValues = (attribute, oldIndex, newIndex) => (state) => {
  const { data } = state.attributes[attribute];

  const isValidIndex = (index) => Number.isInteger(index) && index >= 0 && index < data.length;

  // Drag libraries can report no-op or out-of-range drops; applying those
  // would desync the rendered list from the posted order payload.
  if (oldIndex === newIndex || !isValidIndex(oldIndex) || !isValidIndex(newIndex)) {
    return state;
  }

  return update(state, {
    attributes: {
      [attribute]: {
        data: {
          $set: arrayMove(data, oldIndex, newIndex),
        },
      },
    },
  });
};
